import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Button,
  Badge,
  Skeleton,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Label,
} from "@asm/ui";
import {
  ArrowUpCircle,
  ArrowDownCircle,
  MinusCircle,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  ExternalLink,
  GitCompare,
  Target,
  Clock,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  comparisonsApi,
  scansApi,
  type DiffResponse,
  type RegressionItem,
  type Scan,
} from "@/lib/api";
import { formatModuleName } from "@/lib/utils";

// Map of known backend messages to translation keys
const HEADLINE_TRANSLATIONS: Record<string, string> = {
  "No significant changes detected": "results.noSignificantChanges",
  "Security posture improved": "results.securityImproved",
  "Security posture degraded": "results.securityDegraded",
  "Mixed changes detected": "results.mixedChanges",
};

// Translate known backend headlines to localized strings
function translateHeadline(headline: string, t: (key: string) => string): string {
  const translationKey = HEADLINE_TRANSLATIONS[headline];
  if (translationKey) {
    return t(translationKey);
  }
  return headline;
}

export default function Comparisons() {
  const { t } = useTranslation("comparisons");
  const { t: tCommon } = useTranslation("common");

  const [selectedTarget, setSelectedTarget] = useState<string>("");
  const [scanA, setScanA] = useState<string>("");
  const [scanB, setScanB] = useState<string>("");

  // Fetch completed scans for selection
  const { data: scansData, isLoading: scansLoading } = useQuery({
    queryKey: ["scans", "completed"],
    queryFn: () => scansApi.list({ status: "completed" }),
  });

  const scans = scansData?.scans || [];

  // Get unique targets from scans
  const uniqueTargets = useMemo(() => {
    if (!scans.length) return [];
    const targets = [...new Set(scans.map((scan: Scan) => scan.target))];
    return targets.sort();
  }, [scans]);

  // Filter scans by selected target (sorted newest first)
  const targetScans = useMemo(() => {
    if (!scans.length || !selectedTarget) return [];
    return scans
      .filter((scan: Scan) => scan.target === selectedTarget)
      .sort((a: Scan, b: Scan) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }, [scans, selectedTarget]);

  // Get the selected baseline scan
  const baselineScan = useMemo(() => {
    if (!scanA || !targetScans.length) return null;
    return targetScans.find((scan: Scan) => scan.id === scanA) || null;
  }, [scanA, targetScans]);

  // Filter scans for "current" dropdown - only show scans NEWER than the baseline (sorted newest first)
  const newerScans = useMemo(() => {
    if (!baselineScan) return [];
    const baselineDate = new Date(baselineScan.created_at).getTime();
    return targetScans
      .filter((scan: Scan) => new Date(scan.created_at).getTime() > baselineDate)
      .sort((a: Scan, b: Scan) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }, [baselineScan, targetScans]);

  // Reset scan selections when target changes
  const handleTargetChange = (target: string) => {
    setSelectedTarget(target);
    setScanA("");
    setScanB("");
  };

  // Reset scanB when scanA changes (if scanB is no longer valid)
  const handleScanAChange = (newScanA: string) => {
    setScanA(newScanA);
    // Reset scanB if it's no longer in the valid newer scans list
    if (scanB) {
      const newBaseline = targetScans.find((scan: Scan) => scan.id === newScanA);
      if (newBaseline) {
        const baselineDate = new Date(newBaseline.created_at).getTime();
        const currentScanB = targetScans.find((scan: Scan) => scan.id === scanB);
        if (currentScanB && new Date(currentScanB.created_at).getTime() <= baselineDate) {
          setScanB("");
        }
      }
    }
  };

  // Fetch recent regressions
  const { data: regressions, isLoading: regressionsLoading } = useQuery({
    queryKey: ["regressions", 7],
    queryFn: () => comparisonsApi.listRegressions(7),
  });

  // Fetch comparison when both scans are selected
  const { data: comparison, isLoading: comparisonLoading } = useQuery({
    queryKey: ["comparison", scanA, scanB],
    queryFn: () => comparisonsApi.compareScans(scanA, scanB),
    enabled: !!scanA && !!scanB && scanA !== scanB,
  });

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case "improvement":
        return <ArrowUpCircle className="h-4 w-4 text-green-500" />;
      case "regression":
        return <ArrowDownCircle className="h-4 w-4 text-red-500" />;
      default:
        return <MinusCircle className="h-4 w-4 text-gray-400" />;
    }
  };

  const getAssessmentBadge = (assessment: string) => {
    const config: Record<string, { className: string; icon: React.ReactNode }> = {
      improved: {
        className: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
        icon: <TrendingUp className="h-3 w-3 mr-1" />,
      },
      degraded: {
        className: "bg-red-500/20 text-red-400 border-red-500/30",
        icon: <TrendingDown className="h-3 w-3 mr-1" />,
      },
      stable: {
        className: "bg-muted text-muted-foreground border-border",
        icon: <MinusCircle className="h-3 w-3 mr-1" />,
      },
      mixed: {
        className: "bg-amber-500/20 text-amber-400 border-amber-500/30",
        icon: <AlertTriangle className="h-3 w-3 mr-1" />,
      },
    };
    const { className, icon } = config[assessment] || config.stable;
    return (
      <Badge className={`flex items-center border ${className}`}>
        {icon}
        {t(`assessment.${assessment}`)}
      </Badge>
    );
  };

  return (
    <div className="tactical-grid space-y-8">
      {/* Header */}
      <div className="animate-fade-in-up">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
            <GitCompare className="h-8 w-8 text-purple-400" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{t("title")}</h1>
            <p className="text-muted-foreground">{t("description")}</p>
          </div>
        </div>
      </div>

      {/* Recent Regressions */}
      <div className="animate-fade-in-up stagger-1 rounded-lg border border-border bg-card/50 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <AlertTriangle className="h-5 w-5 text-amber-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">{t("regressions.title")}</h2>
            <p className="text-xs text-muted-foreground">{t("regressions.description")}</p>
          </div>
        </div>
        {regressionsLoading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full rounded-lg" />
            ))}
          </div>
        ) : regressions?.regressions?.length ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left p-3 text-xs text-muted-foreground uppercase tracking-wider font-medium">{tCommon("table.target")}</th>
                  <th className="text-left p-3 text-xs text-muted-foreground uppercase tracking-wider font-medium">{t("table.regressions")}</th>
                  <th className="text-left p-3 text-xs text-muted-foreground uppercase tracking-wider font-medium">{t("table.scoreChange")}</th>
                  <th className="text-left p-3 text-xs text-muted-foreground uppercase tracking-wider font-medium">{t("table.detectedAt")}</th>
                  <th className="w-[50px]"></th>
                </tr>
              </thead>
              <tbody>
                {regressions.regressions.map((regression: RegressionItem, i: number) => (
                  <tr key={regression.latest_scan_id} className={`border-b border-border/50 hover:bg-muted/30 transition-colors animate-fade-in-up stagger-${Math.min(i + 1, 8)}`}>
                    <td className="p-3 font-medium font-mono">{regression.target}</td>
                    <td className="p-3">
                      <Badge className="border bg-red-500/20 text-red-400 border-red-500/30">{regression.regression_count}</Badge>
                    </td>
                    <td className="p-3 font-mono">
                      {typeof regression.overall_score_change === 'number' && (
                        <span className={regression.overall_score_change < 0 ? "text-red-400" : "text-emerald-400"}>
                          {regression.overall_score_change > 0 ? "+" : ""}
                          {regression.overall_score_change}
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-muted-foreground font-mono text-sm">
                      {new Date(regression.comparison_date).toLocaleDateString()}
                    </td>
                    <td className="p-3">
                      <Button variant="ghost" size="icon" asChild className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10">
                        <Link to={`/scans/${regression.latest_scan_id}`}>
                          <ExternalLink className="h-4 w-4" />
                        </Link>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="p-4 rounded-xl bg-muted/30 border border-border mb-4 inline-block">
              <TrendingUp className="h-12 w-12 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground">{t("regressions.empty")}</p>
          </div>
        )}
      </div>

      {/* Compare Scans */}
      <div className="animate-fade-in-up stagger-2 rounded-lg border border-border bg-card/50 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
            <Target className="h-5 w-5 text-cyan-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">{t("compare.title")}</h2>
            <p className="text-xs text-muted-foreground">{t("compare.description")}</p>
          </div>
        </div>
        <div className="space-y-6">
          {scansLoading ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Skeleton className="h-4 w-24 rounded" />
                <Skeleton className="h-10 w-full rounded-lg" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Skeleton className="h-4 w-24 rounded" />
                  <Skeleton className="h-10 w-full rounded-lg" />
                </div>
                <div className="space-y-2">
                  <Skeleton className="h-4 w-24 rounded" />
                  <Skeleton className="h-10 w-full rounded-lg" />
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Target Selection */}
              <div className="space-y-2">
                <Label className="text-muted-foreground">{t("compare.target")}</Label>
                <Select value={selectedTarget} onValueChange={handleTargetChange}>
                  <SelectTrigger className="bg-muted/50 border-border font-mono">
                    <SelectValue placeholder={t("compare.selectTarget")} />
                  </SelectTrigger>
                  <SelectContent>
                    {uniqueTargets.map((target: string) => (
                      <SelectItem key={target} value={target}>
                        {target}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Scan Selection - only show when target is selected */}
              {selectedTarget && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-muted-foreground flex items-center gap-2">
                      <Clock className="h-3 w-3" />
                      {t("compare.scanA")}
                    </Label>
                    <Select value={scanA} onValueChange={handleScanAChange}>
                      <SelectTrigger className="bg-muted/50 border-border font-mono">
                        <SelectValue placeholder={t("compare.selectScan")} />
                      </SelectTrigger>
                      <SelectContent>
                        {targetScans.map((scan: Scan) => (
                          <SelectItem key={scan.id} value={scan.id}>
                            {new Date(scan.created_at).toLocaleString()}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground flex items-center gap-2">
                      <Clock className="h-3 w-3" />
                      {t("compare.scanB")}
                    </Label>
                    <Select
                      value={scanB}
                      onValueChange={setScanB}
                      disabled={!scanA || newerScans.length === 0}
                    >
                      <SelectTrigger className="bg-muted/50 border-border font-mono">
                        <SelectValue placeholder={scanA ? t("compare.selectScan") : t("compare.selectBaselineFirst")} />
                      </SelectTrigger>
                      <SelectContent>
                        {newerScans.map((scan: Scan) => (
                          <SelectItem key={scan.id} value={scan.id}>
                            {new Date(scan.created_at).toLocaleString()}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {scanA && newerScans.length === 0 && (
                      <p className="text-sm text-muted-foreground">{t("compare.noNewerScans")}</p>
                    )}
                  </div>
                </div>
              )}

              {/* No scans message */}
              {selectedTarget && targetScans.length < 2 && (
                <div className="text-center py-4 text-muted-foreground">
                  {t("compare.needMoreScans")}
                </div>
              )}
            </div>
          )}

          {/* Comparison Results */}
          {comparisonLoading && (
            <div className="space-y-3 pt-6 border-t border-border">
              <Skeleton className="h-24 w-full rounded-lg" />
              <Skeleton className="h-48 w-full rounded-lg" />
            </div>
          )}

          {comparison && (
            <ComparisonResults comparison={comparison} t={t} getSeverityIcon={getSeverityIcon} getAssessmentBadge={getAssessmentBadge} />
          )}
        </div>
      </div>
    </div>
  );
}

function ComparisonResults({
  comparison,
  t,
  getSeverityIcon,
  getAssessmentBadge,
}: {
  comparison: DiffResponse;
  t: (key: string) => string;
  getSeverityIcon: (severity: string) => React.ReactNode;
  getAssessmentBadge: (assessment: string) => React.ReactNode;
}) {
  // Safe access to nested properties
  const summary = comparison?.comparison?.summary;
  const keyChanges = summary?.key_changes ?? [];
  const recommendations = summary?.recommendations ?? [];
  const modules = comparison?.modules ?? [];

  return (
    <div className="space-y-6 border-t border-border pt-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="rounded-lg border border-border bg-muted/30 p-4">
          <div className="text-2xl font-bold font-mono">
            {typeof comparison.comparison.overall_score_change === 'number'
              ? (comparison.comparison.overall_score_change > 0 ? "+" : "") +
                comparison.comparison.overall_score_change
              : "—"}
          </div>
          <p className="text-xs text-muted-foreground uppercase tracking-wider">{t("results.scoreChange")}</p>
        </div>
        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4">
          <div className="text-2xl font-bold text-emerald-400 font-mono">
            {comparison.comparison.improvement_count ?? 0}
          </div>
          <p className="text-xs text-emerald-400/70 uppercase tracking-wider">{t("results.improvements")}</p>
        </div>
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4">
          <div className="text-2xl font-bold text-red-400 font-mono">
            {comparison.comparison.regression_count ?? 0}
          </div>
          <p className="text-xs text-red-400/70 uppercase tracking-wider">{t("results.regressions")}</p>
        </div>
        <div className="rounded-lg border border-border bg-muted/30 p-4">
          {getAssessmentBadge(comparison.comparison.overall_assessment ?? "stable")}
          <p className="text-xs text-muted-foreground uppercase tracking-wider mt-2">{t("results.assessment")}</p>
        </div>
      </div>

      {/* Time Delta */}
      {comparison.time_delta?.human_readable && (
        <div className="text-sm text-muted-foreground font-mono">
          {t("results.timeDelta")}: {comparison.time_delta.human_readable}
        </div>
      )}

      {/* Summary Text */}
      {summary && (
        <div className="rounded-lg border border-border bg-muted/30 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-6 w-1 rounded-full bg-purple-500" />
            <h3 className="text-lg font-semibold">{t("results.summary")}</h3>
          </div>
          <div className="space-y-4">
            {summary.headline && (
              <p className="font-medium text-lg">
                {translateHeadline(summary.headline, t)}
              </p>
            )}
            {keyChanges.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-cyan-400 mb-2">{t("results.keyChanges")}</h4>
                <ul className="space-y-1">
                  {keyChanges.map((change: string, i: number) => (
                    <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                      <span className="text-cyan-400 mt-1">•</span>
                      {change}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {recommendations.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-amber-400 mb-2">{t("results.recommendations")}</h4>
                <ul className="space-y-1">
                  {recommendations.map((rec: string, i: number) => (
                    <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                      <span className="text-amber-400 mt-1">•</span>
                      {rec}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Module Comparison - Side by side */}
      {modules.length > 0 && (
        <div className="rounded-lg border border-border bg-muted/30 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-6 w-1 rounded-full bg-cyan-500" />
            <div>
              <h3 className="text-lg font-semibold">{t("results.moduleComparison")}</h3>
              <p className="text-xs text-muted-foreground">{t("results.moduleComparisonDesc")}</p>
            </div>
          </div>
          {/* Module presence table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left p-3 text-xs text-muted-foreground uppercase tracking-wider font-medium w-[200px]">{t("results.module")}</th>
                  <th className="text-center p-3 text-xs text-muted-foreground uppercase tracking-wider font-medium">{t("results.baselineScan")}</th>
                  <th className="text-center p-3 text-xs text-muted-foreground uppercase tracking-wider font-medium">{t("results.currentScan")}</th>
                  <th className="text-center p-3 text-xs text-muted-foreground uppercase tracking-wider font-medium">{t("results.scoreChange")}</th>
                </tr>
              </thead>
              <tbody>
                {modules.map((module) => (
                  <tr key={module.module} className="border-b border-border/50 hover:bg-muted/50 transition-colors">
                    <td className="p-3 font-medium">{formatModuleName(module.module)}</td>
                    <td className="p-3 text-center">
                      {module.present_in_a ? (
                        <Badge className="border bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                          {t("results.included")}
                        </Badge>
                      ) : (
                        <Badge className="border bg-red-500/10 text-red-400 border-red-500/30">
                          {t("results.notIncluded")}
                        </Badge>
                      )}
                    </td>
                    <td className="p-3 text-center">
                      {module.present_in_b ? (
                        <Badge className="border bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                          {t("results.included")}
                        </Badge>
                      ) : (
                        <Badge className="border bg-red-500/10 text-red-400 border-red-500/30">
                          {t("results.notIncluded")}
                        </Badge>
                      )}
                    </td>
                    <td className="p-3 text-center">
                      {module.present_in_a && module.present_in_b && typeof module.score_change === 'number' && module.score_change !== 0 ? (
                        <Badge className={`border font-mono ${module.score_change > 0
                          ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                          : "bg-red-500/20 text-red-400 border-red-500/30"}`}>
                          {module.score_change > 0 ? "+" : ""}{module.score_change}
                        </Badge>
                      ) : module.present_in_a && module.present_in_b ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        <span className="text-muted-foreground">N/A</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Detailed changes for modules present in both scans */}
          {modules.some(m => m.present_in_a && m.present_in_b && (m.changes?.length ?? 0) > 0) && (
            <div className="mt-6 space-y-4">
              <h4 className="font-medium text-cyan-400">{t("results.detailedChanges")}</h4>
              {modules
                .filter(m => m.present_in_a && m.present_in_b && (m.changes?.length ?? 0) > 0)
                .map((module) => (
                  <div key={module.module} className="rounded-lg border border-border bg-card/50 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-medium">{formatModuleName(module.module)}</span>
                      <div className="flex gap-3 text-sm">
                        {(module.improvement_count ?? 0) > 0 && (
                          <span className="text-emerald-400 font-mono">{module.improvement_count} {t("results.improved")}</span>
                        )}
                        {(module.regression_count ?? 0) > 0 && (
                          <span className="text-red-400 font-mono">{module.regression_count} {t("results.regressed")}</span>
                        )}
                      </div>
                    </div>
                    <div className="space-y-2">
                      {module.changes.map((change, i) => (
                        <div key={i} className="flex items-start gap-2 text-sm">
                          {getSeverityIcon(change.severity)}
                          <span className="text-muted-foreground">{change.description}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
